import { createContext, useContext, useEffect } from 'react';
import { getStaffFirstName, getTimeOfDayGreeting } from '../pages/me/actionCenterModel';

// Lets the routed page hand the application toolbar one compact status value.
// The toolbar owns presentation; the page owns the number. Nothing else travels
// through here, which keeps the toolbar message bounded by construction.

const NOOP = () => {};

export const ShellStatusContext = createContext(NOOP);

export function useShellStatusPublisher() {
  return useContext(ShellStatusContext) || NOOP;
}

/**
 * Publishes the current attention count for the toolbar.
 *
 * Pass a finite count to publish it, or null/undefined while it is unknown
 * (loading, unauthorized, off-route) so the toolbar falls back to the greeting
 * alone. The value is cleared on unmount so it never outlives the page.
 */
export function usePublishShellAttentionCount(attentionCount) {
  const publish = useShellStatusPublisher();

  useEffect(() => {
    publish(Number.isFinite(attentionCount)
      ? { attentionCount: Math.max(0, Math.trunc(attentionCount)) }
      : null);

    return () => publish(null);
  }, [attentionCount, publish]);
}

/**
 * Browser-time greeting for the toolbar, built from the signed-in staff
 * profile. Returns '' when no identity resolves, so the toolbar never shows a
 * nameless greeting.
 */
export function getShellGreetingText(profile = null, user = null, now = undefined) {
  const firstName = getStaffFirstName(profile, user);
  if (!firstName) return '';

  return `${getTimeOfDayGreeting(now)}, ${firstName}`;
}

/**
 * Deliberately short and bounded. Detailed attention lives in the /me Needs
 * attention section, never in the toolbar.
 *
 * The unit is areas, not records. One published signal can stand for sixteen
 * states behind pace, so counting "items" here read as a contradiction against
 * the domain counts inside Needs attention.
 */
export function getShellAttentionText(attentionCount) {
  if (!Number.isFinite(attentionCount) || attentionCount < 0) return '';
  if (attentionCount === 0) return 'All clear';
  if (attentionCount === 1) return '1 area needs attention';

  return `${attentionCount} areas need attention`;
}
